import * as React from 'react';
import classnames from 'classnames';
import { createRecordUrl, formatDecimal, formatMoney, formatPercent } from 'utils/formatter';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector, einheitenByCodeSelector } from 'store/lookups/selector';
import { BestellungPosition, defaultBestellungPosition, moveUpBestellungPosition } from 'api/bestellungPosition';
import { EditIcon, DeleteIcon, UpIcon } from '@fluentui/react-icons';
import { Kunde } from 'api/kunde';
import { Bestellung } from 'api/bestellung';
import { formStyles } from 'styles/formStyles';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { getArtikelDetailId } from 'utils/helper';
import { BestellungPositionEdit } from './BestellungPositionEdit';
import { BestellungPositionDelete } from './BestellungPositionDelete';
import { BestellungPositionenHeader } from './BestellungPositionenHeader';

interface BestellungPositionenProps {
    kunde: Kunde;
    bestellung: Bestellung;
    bestellungPositionen: BestellungPosition[];
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    allowEdit?: boolean;
    refetch?: () => void;
}

export const BestellungPositionen: React.FC<BestellungPositionenProps> = ({
    kunde,
    bestellung,
    bestellungPositionen,
    // viewMode,
    withHeader = true,
    allowEdit = false,
    refetch,
}) => {
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);

    const [bestellungPositionEditData, setBestellungPositionEditData] = React.useState<BestellungPosition | null>(null);
    const [bestellungPositionDeleteData, setBestellungPositionDeleteData] = React.useState<BestellungPosition | null>(
        null,
    );

    return (
        <>
            <table className={formStyles.positionenTable}>
                {withHeader && (
                    <BestellungPositionenHeader
                        allowEdit={allowEdit}
                        onAdd={() =>
                            setBestellungPositionEditData({
                                ...defaultBestellungPosition,
                                bNr: bestellung.nr,
                                pos: -1,
                            })
                        }
                    />
                )}
                <tbody>
                    {bestellungPositionen.map((bestellungPosition) => (
                        <tr key={bestellungPosition.pos} className={formStyles.positionenRow}>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenNrCell)}>
                                {bestellungPosition.pos}
                            </td>
                            <td
                                className={classnames(
                                    formStyles.positionenCell,
                                    formStyles.positionenArtikelGruppeCell,
                                )}
                            >
                                {(artikelGruppenByNr[bestellungPosition.artikelGruppeNr] &&
                                    artikelGruppenByNr[bestellungPosition.artikelGruppeNr]?.text) ||
                                    bestellungPosition.artikelGruppeNr}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenArtikelNrCell)}>
                                <span>
                                    {bestellungPosition.artikelGruppeNr && bestellungPosition.artikelNr ? (
                                        <OpenLink
                                            link={createRecordUrl(
                                                'lager/artikel',
                                                undefined,
                                                getArtikelDetailId(
                                                    bestellungPosition.artikelGruppeNr,
                                                    bestellungPosition.artikelNr,
                                                ),
                                                undefined,
                                                false,
                                            )}
                                            label={bestellungPosition.artikelNr}
                                        />
                                    ) : (
                                        ''
                                    )}
                                </span>
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenTextCell)}>
                                {bestellungPosition.posText}
                                {bestellungPosition.posBem ? (
                                    <i>
                                        <br />
                                        {bestellungPosition.posBem}
                                    </i>
                                ) : (
                                    ''
                                )}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenEinhCell)}>
                                {bestellungPosition.posEinh
                                    ? einheitenByCode[bestellungPosition.posEinh].text || bestellungPosition.posEinh
                                    : '?'}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMengeCell)}>
                                {formatDecimal(bestellungPosition.posMenge, 3)}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                {formatDecimal(bestellungPosition.offen, 3)}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                {formatMoney(bestellungPosition.posEPreis, 3)}
                            </td>
                            <td
                                className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}
                                title={'VK-Brutto: ' + formatMoney(bestellungPosition.posBrutto)}
                            >
                                {formatMoney(bestellungPosition.posNetto)}
                            </td>
                            {/*
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                {formatMoney(bestellungPosition.posBrutto)}
                            </td>
                            */}
                            <td
                                className={classnames(formStyles.positionenCell, formStyles.positionenMwstCell)}
                                title={'VK-Brutto: ' + formatMoney(bestellungPosition.posBrutto)}
                            >
                                {formatPercent(bestellungPosition.posMwst)}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenLArtCell)}>
                                {bestellungPosition.lArt}
                            </td>
                            {allowEdit && (
                                <>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenActionCell,
                                        )}
                                    >
                                        {bestellungPosition.pos > 1 && (
                                            <UpIcon
                                                className={formStyles.positionenAction}
                                                onClick={async () => {
                                                    await moveUpBestellungPosition(
                                                        bestellungPosition.bNr,
                                                        bestellungPosition.pos,
                                                    );
                                                    if (refetch) {
                                                        refetch();
                                                    }
                                                }}
                                            />
                                        )}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenActionCell,
                                        )}
                                    >
                                        <EditIcon
                                            className={formStyles.positionenAction}
                                            onClick={() => setBestellungPositionEditData(bestellungPosition)}
                                        />
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenActionCell,
                                        )}
                                    >
                                        <DeleteIcon
                                            className={formStyles.positionenAction}
                                            onClick={() => setBestellungPositionDeleteData(bestellungPosition)}
                                        />
                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
            {bestellungPositionEditData !== null && (
                <BestellungPositionEdit
                    kunde={kunde}
                    bestellung={bestellung}
                    bNr={bestellungPositionEditData.bNr}
                    existingBestellungPositionen={bestellungPositionen}
                    pos={bestellungPositionEditData.pos}
                    onClose={(_refetch) => {
                        setBestellungPositionEditData(null);
                        if (_refetch && refetch) {
                            refetch();
                        }
                    }}
                />
            )}
            {bestellungPositionDeleteData !== null && (
                <BestellungPositionDelete
                    bestellungPosition={bestellungPositionDeleteData}
                    onClose={(_refetch) => {
                        setBestellungPositionDeleteData(null);
                        if (_refetch && refetch) {
                            refetch();
                        }
                    }}
                />
            )}
        </>
    );
};
